<script setup>
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { ROLES } from "@/services/acl";
import {
  fetchAllVerificationAttempts,
  fetchVerificationMedia,
  updateVerificationAttemptStatus,
  rerunVerificationAttemptAi,
  updateUser,
  updateUserStatus,
  addMediaLabel,
  getMediaUrl,
} from "@/services/backofficeClient";

const store = useStore();
const canEditVerification = computed(() =>
  store.getters.hasRole(ROLES.VERIFICATION_EDIT)
);
const canEditUser = computed(() => store.getters.hasRole(ROLES.USER_EDIT));
const canEditMedia = computed(() => store.getters.hasRole(ROLES.MEDIA_EDIT));

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const DEFAULT_PAGE_SIZE = 25;

const attempts = ref([]);
const isLoading = ref(true);
const errorMessage = ref("");
const pageSize = ref(DEFAULT_PAGE_SIZE);
const currentPage = ref(1);
const pagination = ref({
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  totalItems: 0,
  totalPages: 1,
  hasNext: false,
});

const rerunningAttempt = ref({});
const isBulkRerunning = ref(false);
const actionError = ref("");
const actionSelections = ref({});
const applyingActions = ref({});

const showMediaModal = ref(false);
const currentMediaUrl = ref("");
const currentMediaObjectUrl = ref("");

const normalizedPageSize = computed(() => {
  const size = Number(pagination.value.pageSize);
  return Number.isFinite(size) && size > 0 ? size : DEFAULT_PAGE_SIZE;
});
const totalItems = computed(() => {
  const total = Number(pagination.value.totalItems);
  return Number.isFinite(total) && total >= 0 ? total : 0;
});
const totalPages = computed(() => {
  const pages = Number(pagination.value.totalPages);
  return Number.isFinite(pages) && pages > 0 ? pages : 1;
});
const rangeStart = computed(() => {
  if (totalItems.value === 0) return 0;
  return (Math.max(1, currentPage.value) - 1) * normalizedPageSize.value + 1;
});
const rangeEnd = computed(() => {
  if (totalItems.value === 0) return 0;
  return Math.min(currentPage.value * normalizedPageSize.value, totalItems.value);
});
const canGoPrev = computed(() => currentPage.value > 1 && !isLoading.value);
const canGoNext = computed(() => currentPage.value < totalPages.value && !isLoading.value);
const isEmpty = computed(
  () => !isLoading.value && !errorMessage.value && attempts.value.length === 0
);
const isAiRetriggerable = (status) =>
  status === "waiting_for_review" || status === "waiting_for_ai_review";

const waitingForAiRetriggerAttempts = computed(() =>
  attempts.value.filter((attempt) => isAiRetriggerable(attempt.status))
);

const groupedAttempts = computed(() =>
  attempts.value.map((latest) => {
    const history = Array.isArray(latest.history) ? latest.history : [];
    const older = history
      .filter((a) => a.id !== latest.id)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return {
      key: latest.user?.id || latest.userId || latest.id,
      latest,
      older,
      totalAttempts: history.length || 1,
    };
  })
);

const expandedHistory = ref({});
const toggleHistory = (key) => {
  expandedHistory.value = {
    ...expandedHistory.value,
    [key]: !expandedHistory.value[key],
  };
};

const brokenImages = ref(new Set());
const isImageBroken = (url) => brokenImages.value.has(url);
const markImageBroken = (url) => {
  if (!url || brokenImages.value.has(url)) return;
  brokenImages.value = new Set(brokenImages.value).add(url);
};

const formatDate = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateTime = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

const formatStatus = (status) => {
  if (!status) return "--";
  const spaced = status.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const formatGesture = (gesture) => {
  if (!gesture) return null;
  return gesture
    .replace(/([A-Z])/g, " $1")
    .trim()
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
};

const getEnglishCountryName = (countryCode) => {
  if (!countryCode) return null;
  try {
    return new Intl.DisplayNames(["en"], { type: "region" }).of(
      countryCode.toUpperCase()
    );
  } catch {
    return countryCode;
  }
};

const extractLocalized = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (typeof value === "object") return value.en || Object.values(value)[0] || null;
  return null;
};

const formatAddress = (address) => {
  if (!address) return "--";
  const city = extractLocalized(address.city) || extractLocalized(address.locality) || null;
  const country = address.countryCode
    ? getEnglishCountryName(address.countryCode)
    : extractLocalized(address.country) || null;
  return [city, country].filter(Boolean).join(", ") || "--";
};

const getUserPhotoAtoms = (user) => {
  if (!Array.isArray(user?.atoms)) return [];
  return user.atoms
    .filter((atom) => atom?.attribute === "photo" && atom?.mediaPath)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

const getUserPhotos = (user) =>
  getUserPhotoAtoms(user)
    .map((atom) => getMediaUrl(atom.mediaPath))
    .filter(Boolean);

const getPrimaryAvatar = (user) => getUserPhotos(user)[0] || null;

const statusBadgeClass = (status) => {
  if (!status) return "bg-secondary";
  if (status === "approved" || status === "ai_approved") return "bg-success";
  if (status.startsWith("rejected") || status.startsWith("ai_rejected"))
    return "bg-danger";
  if (status === "waiting_for_review" || status === "waiting_for_ai_review")
    return "bg-warning";
  return "bg-secondary";
};

const userStatusBadgeClass = (status) => {
  const normalized = status ? String(status).toLowerCase() : "";
  switch (normalized) {
    case "active":
      return "bg-success";
    case "blocked":
      return "bg-danger";
    case "ghosted":
      return "bg-dark";
    case "deleted":
      return "bg-secondary";
    default:
      return "bg-secondary";
  }
};

const EXCLUDED_STATUS_ACTIONS = new Set([
  "rejected_code",
  "rejected_age",
  "rejected_gesture",
]);

const statusActionOptions = (attempt) =>
  (attempt.adminNextOptions || []).filter(
    (status) => !EXCLUDED_STATUS_ACTIONS.has(status)
  );

const statusActionColor = (status) => {
  if (status === "approved" || status.includes("approved")) return "success";
  if (status.startsWith("rejected")) return "danger";
  return "secondary";
};

const statusActionBtnClass = (status, selected) => {
  const color = statusActionColor(status);
  return selected ? `btn-${color}` : `btn-outline-${color}`;
};

const openMedia = (url) => {
  if (!url) return;
  currentMediaUrl.value = url;
  showMediaModal.value = true;
};

const openVerificationPhoto = async (attempt, index) => {
  if (!attempt?.id || typeof index !== "number") return;
  try {
    const payload = await fetchVerificationMedia(attempt.id, index);
    if (!(payload?.blob instanceof Blob)) return;
    if (currentMediaObjectUrl.value) {
      URL.revokeObjectURL(currentMediaObjectUrl.value);
      currentMediaObjectUrl.value = "";
    }
    const objectUrl = URL.createObjectURL(payload.blob);
    currentMediaObjectUrl.value = objectUrl;
    openMedia(objectUrl);
  } catch (error) {
    console.error("Failed to load verification photo", error);
  }
};

const loadAttempts = async () => {
  isLoading.value = true;
  errorMessage.value = "";
  actionError.value = "";
  try {
    const payload = await fetchAllVerificationAttempts({
      page: currentPage.value,
      pageSize: pageSize.value,
    });
    attempts.value = Array.isArray(payload?.items) ? payload.items : [];
    pagination.value = payload?.pagination ?? {
      page: 1,
      pageSize: pageSize.value,
      totalItems: 0,
      totalPages: 1,
      hasNext: false,
    };
  } catch (error) {
    errorMessage.value =
      error?.message || "Failed to load verification attempts.";
    attempts.value = [];
  } finally {
    isLoading.value = false;
  }
};

const applyFilters = () => {
  currentPage.value = 1;
  loadAttempts();
};

const changePage = (delta) => {
  currentPage.value += delta;
  loadAttempts();
};

const rerunAi = async (attempt) => {
  if (!attempt?.id) return;
  rerunningAttempt.value[attempt.id] = true;
  actionError.value = "";
  try {
    const updated = await rerunVerificationAttemptAi(attempt.id);
    const patch = {
      status: updated.status,
      adminNextOptions: updated.adminNextOptions,
      insights: updated.insights,
      updatedAt: updated.updatedAt,
    };
    attempts.value = attempts.value.map((a) => {
      const isSelf = a.id === attempt.id;
      const historyIdx = Array.isArray(a.history)
        ? a.history.findIndex((h) => h.id === attempt.id)
        : -1;
      if (!isSelf && historyIdx === -1) return a;
      const next = isSelf ? { ...a, ...patch } : a;
      if (historyIdx === -1) return next;
      const updatedHistory = [...next.history];
      updatedHistory[historyIdx] = { ...updatedHistory[historyIdx], ...patch };
      return { ...next, history: updatedHistory };
    });
  } catch (error) {
    actionError.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to retrigger AI check.";
  } finally {
    rerunningAttempt.value[attempt.id] = false;
  }
};

const rerunAiForAllShown = async () => {
  const targets = waitingForAiRetriggerAttempts.value;
  if (targets.length === 0 || isBulkRerunning.value) return;
  isBulkRerunning.value = true;
  actionError.value = "";
  try {
    for (const attempt of targets) {
      await rerunAi(attempt);
    }
  } finally {
    isBulkRerunning.value = false;
  }
};

const updateAttemptUser = (attemptId, patch) => {
  const idx = attempts.value.findIndex((a) => a.id === attemptId);
  if (idx === -1 || !attempts.value[idx].user) return;
  attempts.value[idx] = {
    ...attempts.value[idx],
    user: { ...attempts.value[idx].user, ...patch },
  };
};

const EMPTY_ACTION_STATE = {
  status: "",
  userStatus: "",
  noHumanFace: false,
  requireVerification: false,
  reason: "",
};

const actionState = (attemptId) =>
  actionSelections.value[attemptId] || EMPTY_ACTION_STATE;

const setSelectedStatus = (attemptId, status) => {
  const current = actionState(attemptId);
  const nextStatus = current.status === status ? "" : status;
  actionSelections.value = {
    ...actionSelections.value,
    [attemptId]: { ...current, status: nextStatus },
  };
};

const USER_STATUS_ACTION_REASON = "Verification Issue";

const setUserStatusAction = (attemptId, userStatus) => {
  const current = actionState(attemptId);
  const nextUserStatus = current.userStatus === userStatus ? "" : userStatus;
  actionSelections.value = {
    ...actionSelections.value,
    [attemptId]: {
      ...current,
      userStatus: nextUserStatus,
      reason: nextUserStatus ? USER_STATUS_ACTION_REASON : "",
    },
  };
};

const toggleUserAction = (attemptId, key) => {
  const current = actionState(attemptId);
  actionSelections.value = {
    ...actionSelections.value,
    [attemptId]: { ...current, [key]: !current[key] },
  };
};

const hasSelectedActions = (attemptId) => {
  const state = actionState(attemptId);
  return (
    !!state.status ||
    !!state.userStatus ||
    state.noHumanFace ||
    state.requireVerification
  );
};

const applyActions = async (attempt) => {
  const state = actionState(attempt.id);
  if (!hasSelectedActions(attempt.id) || applyingActions.value[attempt.id]) return;
  const userId = attempt?.user?.id || attempt?.userId;
  applyingActions.value[attempt.id] = true;
  actionError.value = "";
  try {
    if (state.status) {
      const updated = await updateVerificationAttemptStatus(attempt.id, state.status);
      const idx = attempts.value.findIndex((a) => a.id === attempt.id);
      if (idx !== -1) {
        attempts.value[idx] = {
          ...attempts.value[idx],
          status: updated.status,
          adminNextOptions: updated.adminNextOptions,
          insights: updated.insights,
          updatedAt: updated.updatedAt,
        };
      }
    }
    if (state.userStatus) {
      await updateUserStatus(userId, state.userStatus, state.reason, [
        { type: "verificationAttempt", id: attempt.id },
      ]);
      updateAttemptUser(attempt.id, { status: state.userStatus });
    }
    if (state.requireVerification) {
      await updateUser(userId, {
        verificationStatus: "required",
        verificationStatusReason: "Verification requested from verification review",
        verificationStatusReferences: [
          { type: "verificationAttempt", id: attempt.id },
        ],
      });
      updateAttemptUser(attempt.id, { verificationStatus: "required" });
    }
    if (state.noHumanFace) {
      const photoAtoms = getUserPhotoAtoms(attempt.user);
      for (const atom of photoAtoms) {
        await addMediaLabel(atom.mediaPath, "no_human_face");
      }
    }
    actionSelections.value = {
      ...actionSelections.value,
      [attempt.id]: { ...EMPTY_ACTION_STATE },
    };
  } catch (error) {
    actionError.value =
      error?.payload?.message || error?.message || "Failed to apply actions.";
  } finally {
    applyingActions.value[attempt.id] = false;
  }
};

onMounted(loadAttempts);
</script>

<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <!-- Header -->
          <div class="pb-0 card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div class="d-flex align-items-center gap-2 flex-wrap">
              <span v-if="!isLoading && !errorMessage" class="text-secondary text-xs">{{ totalItems }} user{{ totalItems === 1 ? "" : "s" }} pending review</span>
            </div>
            <div class="d-flex align-items-center gap-2 flex-nowrap">
              <button v-if="canEditVerification" type="button" class="btn btn-sm btn-outline-info mb-0 text-nowrap"
                :disabled="isBulkRerunning || waitingForAiRetriggerAttempts.length === 0" @click="rerunAiForAllShown">
                <span v-if="isBulkRerunning" class="spinner-border spinner-border-sm me-1"></span>
                Retrigger AI check ({{ waitingForAiRetriggerAttempts.length }} shown)
              </button>
            </div>
          </div>

          <div v-if="actionError" class="mx-4 mt-3 alert alert-danger py-2 px-3 text-sm mb-0">
            {{ actionError }}
          </div>

          <div class="px-0 pb-2 card-body">
            <!-- Loading -->
            <div v-if="isLoading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>

            <!-- Error -->
            <div v-else-if="errorMessage" class="text-center py-5 text-danger">
              {{ errorMessage }}
            </div>

            <!-- Empty -->
            <div v-else-if="isEmpty" class="text-center py-5 text-secondary">
              No verification attempts found.
            </div>

            <!-- Cards -->
            <div v-else class="px-4">
              <div v-for="group in groupedAttempts" :key="group.key" class="verif-card">
                <div class="verif-card__top">
                  <div class="verif-card__identity">
                    <div class="verif-avatar">
                      <img v-if="getPrimaryAvatar(group.latest.user) && !isImageBroken(getPrimaryAvatar(group.latest.user))"
                        :src="getPrimaryAvatar(group.latest.user)" alt=""
                        @error="markImageBroken(getPrimaryAvatar(group.latest.user))" />
                      <i v-else class="fas fa-user"></i>
                    </div>
                    <div class="min-w-0">
                      <div class="d-flex align-items-center gap-2 flex-wrap">
                        <router-link v-if="group.latest.user?.id" :to="`/users/${group.latest.user.id}`"
                          class="verif-name">{{ group.latest.user.name || "--" }}</router-link>
                        <span v-else class="verif-name">{{ group.latest.user?.name || "--" }}</span>
                        <span class="badge badge-sm text-white" :class="statusBadgeClass(group.latest.status)">{{
                          formatStatus(group.latest.status) }}</span>
                        <span v-if="group.latest.user?.status && group.latest.user.status.toLowerCase() !== 'active'"
                          class="badge badge-sm text-white" :class="userStatusBadgeClass(group.latest.user.status)">{{
                            formatStatus(group.latest.user.status) }}</span>
                        <span v-if="group.totalAttempts > 1" class="text-xs text-secondary">· {{ group.totalAttempts }} tries</span>
                      </div>
                      <div class="text-xs text-secondary">
                        {{ group.latest.user?.age ?? "--" }} yo ({{ formatDate(group.latest.user?.birthdate) }}) · {{
                          group.latest.user?.gender ?? "--" }} · {{ formatAddress(group.latest.user?.address) }}
                      </div>
                      <div class="text-xxs text-secondary">Submitted {{ formatDateTime(group.latest.createdAt) }}</div>
                    </div>
                  </div>

                  <div v-if="canEditVerification && statusActionOptions(group.latest).length"
                    class="verif-card__decision">
                    <button v-for="status in statusActionOptions(group.latest)" :key="status" type="button"
                      class="btn btn-sm mb-0"
                      :class="statusActionBtnClass(status, actionState(group.latest.id).status === status)"
                      :disabled="!!applyingActions[group.latest.id]"
                      @click="setSelectedStatus(group.latest.id, status)">
                      {{ formatStatus(status) }}
                    </button>
                  </div>
                </div>

                <div class="verif-card__media">
                  <div v-if="getUserPhotos(group.latest.user).length" class="verif-thumb-group">
                    <span class="verif-thumb-label">Profile</span>
                    <div class="verif-thumb-row">
                      <div v-for="(url, i) in getUserPhotos(group.latest.user)" :key="i" class="verif-thumb"
                        @click="openMedia(url)">
                        <img v-if="!isImageBroken(url)" :src="url" alt="" @error="markImageBroken(url)" />
                        <i v-else class="fas fa-image"></i>
                      </div>
                    </div>
                  </div>

                  <div class="verif-thumb-group">
                    <span class="verif-thumb-label">Attempt</span>
                    <div class="verif-thumb-row align-items-center">
                      <template v-if="group.latest.verificationMediaPaths?.length">
                        <template v-for="(path, idx) in group.latest.verificationMediaPaths"
                          :key="`${group.latest.id}-media-${idx}`">
                          <div v-if="getMediaUrl(path) && path.includes('photo')" class="verif-thumb"
                            @click="openMedia(getMediaUrl(path))">
                            <img v-if="!isImageBroken(getMediaUrl(path))" :src="getMediaUrl(path)" alt=""
                              @error="markImageBroken(getMediaUrl(path))" />
                            <i v-else class="fas fa-image"></i>
                          </div>
                          <audio v-else-if="getMediaUrl(path) && path.includes('voice')" :src="getMediaUrl(path)"
                            controls></audio>
                        </template>
                      </template>
                      <template v-else-if="group.latest.verificationPhotoPaths?.length">
                        <button v-for="(_, idx) in group.latest.verificationPhotoPaths"
                          :key="`${group.latest.id}-photo-${idx}`" type="button"
                          class="btn btn-xs btn-outline-success mb-0"
                          @click="openVerificationPhoto(group.latest, idx)">Photo {{ idx + 1 }}</button>
                      </template>
                      <span v-else class="text-sm text-secondary">No attempt media</span>

                      <span v-if="group.latest.verificationCode" class="badge bg-light text-dark border">{{
                        group.latest.verificationCode }}</span>
                      <span v-if="group.latest.verificationGesture" class="badge bg-light text-dark border">{{
                        formatGesture(group.latest.verificationGesture) }}</span>
                    </div>
                  </div>
                </div>

                <div v-if="group.latest.insights" class="verif-insights">
                  <i class="fas fa-robot"></i>
                  <span>{{ group.latest.insights }}</span>
                  <button v-if="canEditVerification && isAiRetriggerable(group.latest.status)" type="button"
                    class="btn btn-icon-only btn-sm btn-outline-info mb-0 flex-shrink-0 ms-auto" title="Rerun AI check"
                    :disabled="!!rerunningAttempt[group.latest.id]" @click="rerunAi(group.latest)">
                    <span v-if="rerunningAttempt[group.latest.id]" class="spinner-border spinner-border-sm"></span>
                    <i v-else class="fas fa-sync"></i>
                  </button>
                </div>

                <div v-if="canEditVerification || canEditUser || canEditMedia" class="verif-card__footer flex-column">
                  <div v-if="canEditUser || canEditMedia" class="d-flex flex-wrap gap-1">
                    <button v-if="canEditUser" type="button" class="btn btn-xs mb-0"
                      :class="actionState(group.latest.id).userStatus === 'blocked' ? 'btn-danger' : 'btn-outline-secondary'"
                      :disabled="!!applyingActions[group.latest.id] || group.latest.user?.status === 'blocked'"
                      @click="setUserStatusAction(group.latest.id, 'blocked')">
                      Block
                    </button>
                    <button v-if="canEditUser" type="button" class="btn btn-xs mb-0"
                      :class="actionState(group.latest.id).userStatus === 'ghosted' ? 'btn-warning' : 'btn-outline-secondary'"
                      :disabled="!!applyingActions[group.latest.id] || group.latest.user?.status === 'ghosted'"
                      @click="setUserStatusAction(group.latest.id, 'ghosted')">
                      Ghost
                    </button>
                    <button v-if="canEditMedia" type="button" class="btn btn-xs mb-0"
                      :class="actionState(group.latest.id).noHumanFace ? 'btn-danger' : 'btn-outline-secondary'"
                      :disabled="!!applyingActions[group.latest.id] || getUserPhotos(group.latest.user).length === 0"
                      @click="toggleUserAction(group.latest.id, 'noHumanFace')">
                      No Face label
                    </button>
                    <button v-if="canEditUser" type="button" class="btn btn-xs mb-0"
                      :class="actionState(group.latest.id).requireVerification ? 'btn-danger' : 'btn-outline-secondary'"
                      :disabled="!!applyingActions[group.latest.id] || group.latest.user?.verificationStatus === 'required'"
                      @click="toggleUserAction(group.latest.id, 'requireVerification')">
                      Require verification
                    </button>
                  </div>
                  <button type="button" class="btn btn-sm btn-success mb-0 ms-auto"
                    :disabled="!hasSelectedActions(group.latest.id) || !!applyingActions[group.latest.id]"
                    @click="applyActions(group.latest)">
                    <span v-if="applyingActions[group.latest.id]" class="spinner-border spinner-border-sm me-1"></span>
                    Apply actions
                  </button>
                </div>

                <button v-if="group.older.length" type="button" class="verif-history-toggle"
                  @click="toggleHistory(group.key)">
                  <i class="fas" :class="expandedHistory[group.key] ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                  {{ expandedHistory[group.key] ? "Hide" : "Show" }} {{ group.older.length }} earlier attempt{{
                    group.older.length === 1 ? "" : "s" }}
                </button>

                <div v-if="group.older.length && expandedHistory[group.key]" class="verif-history">
                  <div v-for="old in group.older" :key="old.id" class="verif-history__row">
                    <span class="badge badge-sm text-white" :class="statusBadgeClass(old.status)">{{
                      formatStatus(old.status) }}</span>
                    <span class="text-xxs text-secondary">{{ formatDateTime(old.createdAt) }}</span>

                    <template v-if="old.verificationMediaPaths?.length">
                      <template v-for="(path, idx) in old.verificationMediaPaths" :key="`${old.id}-media-${idx}`">
                        <div v-if="getMediaUrl(path) && path.includes('photo')" class="verif-thumb verif-thumb--sm"
                          @click="openMedia(getMediaUrl(path))">
                          <img v-if="!isImageBroken(getMediaUrl(path))" :src="getMediaUrl(path)" alt=""
                            @error="markImageBroken(getMediaUrl(path))" />
                          <i v-else class="fas fa-image"></i>
                        </div>
                      </template>
                    </template>
                    <template v-else-if="old.verificationPhotoPaths?.length">
                      <button v-for="(_, idx) in old.verificationPhotoPaths" :key="`${old.id}-photo-${idx}`"
                        type="button" class="btn btn-xs btn-outline-success mb-0 py-0 px-1"
                        @click="openVerificationPhoto(old, idx)">P{{ idx + 1 }}</button>
                    </template>

                    <span v-if="old.verificationCode" class="text-xxs text-secondary">Code: {{
                      old.verificationCode }}</span>
                    <span v-if="old.verificationGesture" class="text-xxs text-secondary">{{
                      formatGesture(old.verificationGesture) }}</span>

                    <span v-if="old.insights" class="text-xxs text-secondary text-truncate verif-history__insights"
                      :title="old.insights">{{ old.insights }}</span>

                    <button v-if="canEditVerification && isAiRetriggerable(old.status)" type="button"
                      class="btn btn-icon-only btn-xs btn-outline-info mb-0 py-0 px-1 flex-shrink-0 ms-auto"
                      title="Rerun AI check" :disabled="!!rerunningAttempt[old.id]" @click="rerunAi(old)">
                      <span v-if="rerunningAttempt[old.id]" class="spinner-border spinner-border-sm"
                        style="width: 0.7rem; height: 0.7rem"></span>
                      <i v-else class="fas fa-sync" style="font-size: 10px"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Pagination -->
            <div v-if="!isLoading && !errorMessage && attempts.length > 0"
              class="d-flex justify-content-between align-items-center px-4 py-3 flex-wrap gap-2">
              <span class="text-secondary text-xs">
                Showing {{ rangeStart }}–{{ rangeEnd }} of {{ totalItems }} users
              </span>
              <div class="d-flex gap-3 align-items-center flex-wrap">
                <div class="d-flex align-items-center gap-1">
                  <label class="mb-0 text-xs text-secondary text-nowrap">Per page</label>
                  <select v-model="pageSize" class="form-select form-select-sm" style="width: 75px"
                    @change="applyFilters">
                    <option v-for="n in PAGE_SIZE_OPTIONS" :key="n" :value="n">{{ n }}</option>
                  </select>
                </div>
                <div class="d-flex gap-2 align-items-center">
                  <button class="btn btn-sm btn-outline-secondary mb-0" :disabled="!canGoPrev"
                    @click="changePage(-1)">Previous</button>
                  <span class="text-xs text-secondary">Page {{ currentPage }} / {{ totalPages }}</span>
                  <button class="btn btn-sm btn-outline-secondary mb-0" :disabled="!canGoNext"
                    @click="changePage(1)">Next</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Media modal -->
  <div v-if="showMediaModal" class="modal fade show d-block" style="background: rgba(0,0,0,0.75)"
    @click.self="showMediaModal = false">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-header py-2 px-3">
          <button type="button" class="btn-close ms-auto" @click="showMediaModal = false"></button>
        </div>
        <div class="modal-body text-center p-2">
          <img v-if="currentMediaUrl" :src="currentMediaUrl" alt="media" class="img-fluid rounded"
            style="max-height: 80vh" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.verif-card {
  border: 1px solid #e9ecef;
  border-radius: 0.75rem;
  padding: 1rem 1.25rem;
  margin-bottom: 1rem;
}

.verif-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.verif-card__identity {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  min-width: 0;
}

.verif-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  overflow: hidden;
  background: #f1f3f5;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #adb5bd;
  font-size: 1.25rem;
}

.verif-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.verif-name {
  font-size: 0.875rem;
  font-weight: 700;
  margin-bottom: 0;
}

.verif-card__decision {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.verif-card__media {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #f1f3f5;
}

.verif-thumb-group {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.verif-thumb-label {
  font-size: 0.625rem;
  text-transform: uppercase;
  font-weight: 700;
  color: #adb5bd;
  letter-spacing: 0.03em;
}

.verif-thumb-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.verif-thumb {
  width: 96px;
  height: 96px;
  border-radius: 0.5rem;
  border: 1px solid #e9ecef;
  background: #f8f9fa;
  overflow: hidden;
  cursor: zoom-in;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ced4da;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.verif-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.verif-thumb--sm {
  width: 36px;
  height: 36px;
  font-size: 0.875rem;
  cursor: zoom-in;
}

.verif-insights {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding: 0.5rem 0.75rem;
  background: #f8f9fa;
  border-left: 3px solid #17c1e8;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  line-height: 1.4;
  color: #495057;
}

.verif-insights i {
  color: #17c1e8;
  margin-top: 0.15rem;
}

.verif-card__footer {
  display: flex;
  align-items: end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #f1f3f5;
}

.verif-history-toggle {
  border: none;
  background: none;
  color: #17c1e8;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.5rem 0 0;
  margin-top: 0.25rem;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  cursor: pointer;
}

.verif-history-toggle:hover {
  text-decoration: underline;
}

.verif-history {
  margin-top: 0.5rem;
  border-top: 1px dashed #e9ecef;
}

.verif-history__row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f8f9fa;
  font-size: 0.75rem;
}

.verif-history__insights {
  max-width: 320px;
}

.min-w-0 {
  min-width: 0;
}
</style>
